import type {
  ResumeHydrationState,
  ResumeSessionResponse,
} from "@/D2C/features/auth/utils/resumeJourney";
import {
  firstNonEmpty,
  isRecord,
  readRecordValue,
  toBoolean,
  toStringValue,
} from "@/shared/utils/hydrationValues";

const readValue = readRecordValue;
const toText = toStringValue;

const toTravelGender = (value: unknown): "Male" | "Female" | "" => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();

  if (normalized === "male" || normalized === "m") return "Male";
  if (normalized === "female" || normalized === "f") return "Female";
  return "";
};

const toBeneficiaryRelation = (value: unknown): "Legal Heir" | "Other" | "" => {
  const text = String(value || "")
    .trim()
    .toLowerCase();
  if (text === "legal heir" || text === "legal_heir") return "Legal Heir";
  if (text === "other") return "Other";
  return "";
};

const toLifeGender = (value: unknown): "male" | "female" | "" => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();

  if (normalized === "male" || normalized === "m") return "male";
  if (normalized === "female" || normalized === "f") return "female";
  return "";
};

const toLifeSmoker = (value: unknown): "yes" | "no" | "" => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();

  if (["yes", "y", "true", "1"].includes(normalized)) return "yes";
  if (["no", "n", "false", "0"].includes(normalized)) return "no";
  return "";
};

const getTravelAdditionalPayload = (sessionData: ResumeSessionResponse) => {
  const stepSix = sessionData?.step_6_post_request;

  return (
    readValue(
      stepSix,
      "additionalPersonalDetails",
      "additional_personal_details",
      "additionalInfo",
      "additional_info"
    ) || stepSix
  );
};

const getPrimaryTravellerInfo = (payload: unknown) => {
  const travellersMoreInfo = readValue(payload, "travellers_more_info", "travellersMoreInfo");
  if (!Array.isArray(travellersMoreInfo) || travellersMoreInfo.length === 0) return undefined;

  const primary = travellersMoreInfo[0];
  return isRecord(primary) ? primary : undefined;
};

const getAdditionalTravellersInfo = (payload: unknown) => {
  const travellersMoreInfo = readValue(payload, "travellers_more_info", "travellersMoreInfo");
  if (!Array.isArray(travellersMoreInfo) || travellersMoreInfo.length <= 1) return [];

  return travellersMoreInfo.slice(1).filter(isRecord);
};

const patchTravelDetails = (
  sessionData: ResumeSessionResponse,
  hydration: ResumeHydrationState
) => {
  const travel = hydration.travel;
  if (!travel) return travel;

  const stepTravel = sessionData?.step_2_pre_request?.travel_details;
  const quoteTravel = sessionData?.quote_request?.travel_details;
  const rawTravel = sessionData?.raw_request?.travel_details;

  const fromCountry = firstNonEmpty(
    readValue(stepTravel, "departure", "fromCountry", "from_country", "from", "start"),
    readValue(quoteTravel, "departure", "fromCountry", "from_country", "from", "start"),
    readValue(rawTravel, "departure", "fromCountry", "from_country", "from", "start"),
    travel.travelDetails.fromCountry
  );

  const destination = firstNonEmpty(
    readValue(
      stepTravel,
      "destination",
      "toCountry",
      "to_country",
      "to",
      "end",
      "destination_country"
    ),
    readValue(
      quoteTravel,
      "destination",
      "toCountry",
      "to_country",
      "to",
      "end",
      "destination_country"
    ),
    readValue(
      rawTravel,
      "destination",
      "toCountry",
      "to_country",
      "to",
      "end",
      "destination_country"
    ),
    travel.travelDetails.destination
  );

  return {
    ...travel,
    travelDetails: {
      ...travel.travelDetails,
      fromCountry: toText(fromCountry, travel.travelDetails.fromCountry),
      destination: toText(destination, travel.travelDetails.destination),
    },
  };
};

const patchTravelAdditionalInfo = (
  sessionData: ResumeSessionResponse,
  hydration: ResumeHydrationState
) => {
  const travel = hydration.travel;
  if (!travel) return travel;

  const payload = getTravelAdditionalPayload(sessionData);
  const primary = getPrimaryTravellerInfo(payload);
  const declarations = readValue(payload, "declarations");
  const additionalTravellers = getAdditionalTravellersInfo(payload);

  const mergedAdditionalInfo = {
    ...travel.additionalInfo,
    gender: toTravelGender(
      firstNonEmpty(readValue(primary, "gender"), readValue(payload, "gender"))
    ),
    nationality: toText(
      firstNonEmpty(readValue(primary, "nationality"), readValue(payload, "nationality")),
      travel.additionalInfo.nationality
    ),
    cityOfResidence: toText(
      firstNonEmpty(
        readValue(primary, "cityOfResidence", "city_of_residence", "city"),
        readValue(payload, "cityOfResidence", "city_of_residence", "city")
      ),
      travel.additionalInfo.cityOfResidence
    ),
    address: toText(
      firstNonEmpty(readValue(primary, "address"), readValue(payload, "address")),
      travel.additionalInfo.address
    ),
    emiratesId: toText(
      firstNonEmpty(
        readValue(primary, "emiratesId", "emirates_id"),
        readValue(payload, "emiratesId", "emirates_id")
      ),
      travel.additionalInfo.emiratesId
    ),
    passportNumber: toText(
      firstNonEmpty(
        readValue(primary, "passportNo", "passportNumber", "passport_number"),
        readValue(payload, "passportNo", "passportNumber", "passport_number")
      ),
      travel.additionalInfo.passportNumber
    ),
    sourceOfFund: toText(
      firstNonEmpty(
        readValue(primary, "sourceOfFunds", "sourceOfFund", "source_of_fund"),
        readValue(payload, "sourceOfFunds", "sourceOfFund", "source_of_fund")
      ),
      travel.additionalInfo.sourceOfFund
    ),
    beneficiaryName: toText(
      firstNonEmpty(
        readValue(primary, "beneficiaryName", "beneficiary_name"),
        readValue(payload, "beneficiaryName", "beneficiary_name")
      ),
      travel.additionalInfo.beneficiaryName
    ),
    beneficiaryRelation: toBeneficiaryRelation(
      firstNonEmpty(
        readValue(
          primary,
          "beneficiaryRelationship",
          "beneficiaryRelation",
          "beneficiary_relationship"
        ),
        readValue(
          payload,
          "beneficiaryRelationship",
          "beneficiaryRelation",
          "beneficiary_relationship"
        )
      )
    ),
    uaeResidentConfirm: toBoolean(
      firstNonEmpty(
        readValue(declarations, "isUaeResidentConfirmed"),
        readValue(payload, "uaeResidentConfirm", "uae_resident_confirm")
      ),
      travel.additionalInfo.uaeResidentConfirm
    ),
    travellers:
      additionalTravellers.length > 0
        ? additionalTravellers.map((traveller) => ({
            gender: toTravelGender(readValue(traveller, "gender")),
            nationality: toText(readValue(traveller, "nationality")),
            cityOfResidence: toText(
              readValue(traveller, "cityOfResidence", "city_of_residence", "city")
            ),
            address: toText(readValue(traveller, "address")),
            emiratesId: toText(readValue(traveller, "emiratesId", "emirates_id")),
            passportNumber: toText(
              readValue(traveller, "passportNo", "passportNumber", "passport_number")
            ),
            sourceOfFund: toText(
              readValue(traveller, "sourceOfFunds", "sourceOfFund", "source_of_fund")
            ),
            beneficiaryName: toText(readValue(traveller, "beneficiaryName", "beneficiary_name")),
            beneficiaryRelation: toBeneficiaryRelation(
              readValue(
                traveller,
                "beneficiaryRelationship",
                "beneficiaryRelation",
                "beneficiary_relationship"
              )
            ),
            uaeResidentConfirm: toBoolean(
              readValue(declarations, "isUaeResidentConfirmed"),
              travel.additionalInfo.uaeResidentConfirm
            ),
          }))
        : travel.additionalInfo.travellers || [],
  };

  return {
    ...travel,
    additionalInfo: mergedAdditionalInfo,
  };
};

const patchLifePlanDetails = (
  sessionData: ResumeSessionResponse,
  hydration: ResumeHydrationState
) => {
  const life = hydration.life;
  if (!life) return life;

  const stepLife = (sessionData?.step_2_pre_request as Record<string, unknown> | undefined)
    ?.life_insurance as Record<string, unknown> | undefined;
  const quoteLife = (sessionData?.quote_request as Record<string, unknown> | undefined)
    ?.life_insurance as Record<string, unknown> | undefined;
  const rawLife = (sessionData?.raw_request as Record<string, unknown> | undefined)
    ?.life_insurance as Record<string, unknown> | undefined;

  return {
    ...life,
    planDetails: {
      ...life.planDetails,
      lifeAssuredDob: toText(
        firstNonEmpty(
          readValue(stepLife, "life_assured_date_of_birth", "lifeAssuredDob"),
          readValue(quoteLife, "life_assured_date_of_birth", "lifeAssuredDob"),
          readValue(rawLife, "life_assured_date_of_birth", "lifeAssuredDob"),
          life.planDetails.lifeAssuredDob
        ),
        life.planDetails.lifeAssuredDob
      ),
      lifeAssuredGender: toLifeGender(
        firstNonEmpty(
          readValue(stepLife, "life_assured_gender", "lifeAssuredGender"),
          readValue(quoteLife, "life_assured_gender", "lifeAssuredGender"),
          readValue(rawLife, "life_assured_gender", "lifeAssuredGender"),
          life.planDetails.lifeAssuredGender
        )
      ),
      smoker: toLifeSmoker(
        firstNonEmpty(
          readValue(stepLife, "smoker"),
          readValue(quoteLife, "smoker"),
          readValue(rawLife, "smoker"),
          life.planDetails.smoker
        )
      ),
      planOption: toText(
        firstNonEmpty(
          readValue(stepLife, "plan_option", "planOption"),
          readValue(quoteLife, "plan_option", "planOption"),
          readValue(rawLife, "plan_option", "planOption"),
          life.planDetails.planOption
        ),
        life.planDetails.planOption
      ),
      sumAssured: toText(
        firstNonEmpty(
          readValue(stepLife, "sum_assured", "sumAssured"),
          readValue(quoteLife, "sum_assured", "sumAssured"),
          readValue(rawLife, "sum_assured", "sumAssured"),
          life.planDetails.sumAssured
        ),
        life.planDetails.sumAssured
      ),
      policyTerm: toText(
        firstNonEmpty(
          readValue(stepLife, "policy_term", "policyTerm"),
          readValue(quoteLife, "policy_term", "policyTerm"),
          readValue(rawLife, "policy_term", "policyTerm"),
          life.planDetails.policyTerm
        ),
        life.planDetails.policyTerm
      ),
      premiumPaymentTerm: toText(
        firstNonEmpty(
          readValue(stepLife, "premium_payment_term", "premiumPaymentTerm"),
          readValue(quoteLife, "premium_payment_term", "premiumPaymentTerm"),
          readValue(rawLife, "premium_payment_term", "premiumPaymentTerm"),
          life.planDetails.premiumPaymentTerm
        ),
        life.planDetails.premiumPaymentTerm
      ),
      premiumPaymentFrequency: toText(
        firstNonEmpty(
          readValue(stepLife, "premium_payment_frequency", "premiumPaymentFrequency"),
          readValue(quoteLife, "premium_payment_frequency", "premiumPaymentFrequency"),
          readValue(rawLife, "premium_payment_frequency", "premiumPaymentFrequency"),
          life.planDetails.premiumPaymentFrequency
        ),
        life.planDetails.premiumPaymentFrequency
      ),
      category: toText(
        firstNonEmpty(
          readValue(stepLife, "category"),
          readValue(quoteLife, "category"),
          readValue(rawLife, "category"),
          life.planDetails.category
        ),
        life.planDetails.category
      ),
      payoutType: toText(
        firstNonEmpty(
          readValue(stepLife, "payout_type", "payoutType"),
          readValue(quoteLife, "payout_type", "payoutType"),
          readValue(rawLife, "payout_type", "payoutType"),
          life.planDetails.payoutType
        ),
        life.planDetails.payoutType
      ),
      payoutTerm: toText(
        firstNonEmpty(
          readValue(stepLife, "payout_term", "payoutTerm"),
          readValue(quoteLife, "payout_term", "payoutTerm"),
          readValue(rawLife, "payout_term", "payoutTerm"),
          life.planDetails.payoutTerm
        ),
        life.planDetails.payoutTerm
      ),
      lumpsumPercentage: toText(
        firstNonEmpty(
          readValue(stepLife, "lumpsum_percentage", "lumpsumPercentage"),
          readValue(quoteLife, "lumpsum_percentage", "lumpsumPercentage"),
          readValue(rawLife, "lumpsum_percentage", "lumpsumPercentage"),
          life.planDetails.lumpsumPercentage
        ),
        life.planDetails.lumpsumPercentage
      ),
      accidentalDeathSumAssured: toText(
        firstNonEmpty(
          readValue(stepLife, "accidental_death_sum_assured", "accidentalDeathSumAssured"),
          readValue(quoteLife, "accidental_death_sum_assured", "accidentalDeathSumAssured"),
          readValue(rawLife, "accidental_death_sum_assured", "accidentalDeathSumAssured"),
          life.planDetails.accidentalDeathSumAssured
        ),
        life.planDetails.accidentalDeathSumAssured
      ),
    },
  };
};

export function applyB2CSessionHydrationOverrides(
  sessionData: ResumeSessionResponse,
  hydration: ResumeHydrationState
): ResumeHydrationState {
  if (hydration.life) {
    return {
      ...hydration,
      life: patchLifePlanDetails(sessionData, hydration),
    };
  }

  if (!hydration.travel) return hydration;

  const patchedTravelDetails = patchTravelDetails(sessionData, hydration);
  const patchedTravelAdditionalInfo = patchTravelAdditionalInfo(sessionData, {
    ...hydration,
    travel: patchedTravelDetails,
  });

  return {
    ...hydration,
    travel: patchedTravelAdditionalInfo,
  };
}
